import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useToast } from '@/components/ui';
import {
    applyBulkLevelAssign,
    OrgAdminApiError,
    previewBulkLevelAssign,
    type BulkLevelImpactRow,
    type BulkLevelPreview,
} from '@/api/orgAdmin';
import type { ScopedAccountsVM } from './useScopedAccounts';

/**
 * EAA-1839: the bulk Level-assign dialog of frame 35 — preview, the
 * replace-opt-in, and the narrowed apply call.
 *
 * THE ONE RULE THAT MATTERS HERE. `POST .../assignments/bulk-apply` takes a FLAT
 * `{user_subs, level_id}` and nothing else: `AssignmentService.bulk_apply` re-runs
 * `bulk_preview` internally and then writes BOTH the `new` and the `changed`
 * buckets unconditionally — there is no "skip the changed ones" flag in the body
 * (verified against its signature, `bulk_apply(org_id, level_id, user_subs,
 * actor_sub)`). So frame 35's opt-in checkbox CANNOT be expressed as a request
 * flag. It has to be expressed as a narrowed id list, computed here:
 *
 *     apply subs = preview.new
 *                + (replaceChanged ? preview.changed.map(user_sub) : [])
 *
 * `preview.skipped` is never sent. Those subs already hold this exact level, so
 * including them would only echo them back in the response's `skipped` — the
 * dialog states the number instead ("Đã có Level này — bỏ qua").
 *
 * Leaving the box unticked therefore genuinely does not touch the `changed` rows:
 * their subs are absent from the request, so nothing about them is revoked or
 * recreated. Sending the full selection and hoping the server filters would
 * silently REVOKE-AND-REPLACE every one of those grants.
 */
export function useBulkLevelAssign(list: ScopedAccountsVM) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const toast = useToast();

    const { orgId, bulkUserSubs, invalidateList, clearSelection } = list;

    const [open, setOpen] = useState(false);
    const [levelId, setLevelId] = useState<string>('');
    const [replaceChanged, setReplaceChanged] = useState(false);

    const close = useCallback(() => setOpen(false), []);
    const openDialog = useCallback(() => {
        setReplaceChanged(false);
        setOpen(true);
    }, []);

    // Re-opting-in has to be deliberate every time a different level is targeted:
    // "replace 42 existing grants" is a decision about THAT level, not a sticky
    // preference to carry over to the next one.
    useEffect(() => {
        setReplaceChanged(false);
    }, [levelId]);

    // The dialog closes itself if the selection it was opened for disappears
    // (a filter change clears the selection — see `useScopedAccounts`).
    useEffect(() => {
        if (open && bulkUserSubs.length === 0) setOpen(false);
    }, [open, bulkUserSubs.length]);

    const previewQuery = useQuery({
        queryKey: ['scoped-accounts-bulk-preview', accessToken, orgId, levelId, bulkUserSubs],
        queryFn: () =>
            previewBulkLevelAssign(config.apiBase, accessToken as string, orgId as string, {
                userSubs: bulkUserSubs,
                levelId,
            }),
        // Writes nothing (`bulk_preview` is pure), so running it on every level
        // change is safe — but never with an empty list: `user_subs` is
        // `allow_empty=False`, which would be a 400 rather than an empty preview.
        enabled: open && Boolean(orgId) && Boolean(levelId) && bulkUserSubs.length > 0,
        retry: false,
        staleTime: 0,
    });

    const preview: BulkLevelPreview | null = previewQuery.data ?? null;

    const changedSubs = useMemo(
        () => (preview?.changed ?? []).map((entry) => entry.user_sub),
        [preview]
    );

    /**
     * EAA-1839: `impact` DE-DUPLICATED per application.
     *
     * Verified against the running portal-api, not assumed:
     * `AssignmentService._impact_preview` loops
     * `OrganizationAppScopeService.list_for_organization(org_id)`, which yields one
     * row per (organization, application, ACH-tenant-id). An org with four ACH
     * tenant scopes therefore gets FOUR byte-identical `ach` entries back — the
     * per-app resolution is the same every time, since
     * `resolve_for_provisioning(level.role_key, application_id, org_id)` is called
     * with identical arguments for each. Rendering the raw array would repeat the
     * same "ach → sys-editor" line four times AND reuse `application_id` as a React
     * key four times. Frame 35 shows one line per application, which is also the
     * only reading that means anything, so the first entry per `application_id`
     * wins.
     */
    const impactRows = useMemo(() => {
        const byApp = new Map<string, BulkLevelImpactRow>();
        for (const row of preview?.impact ?? []) {
            if (!byApp.has(row.application_id)) byApp.set(row.application_id, row);
        }
        return [...byApp.values()];
    }, [preview]);

    /** The exact list sent to `bulk-apply` — see this hook's docstring. */
    const applySubs = useMemo(() => {
        if (!preview || !preview.valid_level) return [];
        return replaceChanged ? [...preview.new, ...changedSubs] : [...preview.new];
    }, [preview, replaceChanged, changedSubs]);

    const applyMutation = useMutation({
        mutationFn: () =>
            applyBulkLevelAssign(config.apiBase, accessToken as string, orgId as string, {
                userSubs: applySubs,
                levelId,
            }),
        onSuccess: (result) => {
            setOpen(false);
            clearSelection();
            invalidateList();
            // `skipped` here is the SERVER's own count (a race against a concurrent
            // grant, or a sub that already held the level). The rows this dialog
            // deliberately left out were never sent, so they are reported
            // separately by the dialog rather than folded into this number.
            if (result.skipped.length > 0) {
                toast.error(
                    t('console.hierarchy.scopedAccounts.bulkAssign.applyPartialMessage', {
                        applied: result.applied.length,
                        skipped: result.skipped.length,
                    })
                );
                return;
            }
            toast.success(
                t('console.hierarchy.scopedAccounts.bulkAssign.applySuccessMessage', {
                    count: result.applied.length,
                })
            );
        },
        onError: (error: unknown) => {
            toast.error(
                error instanceof OrgAdminApiError
                    ? error.message
                    : t('console.hierarchy.scopedAccounts.bulkAssign.applyErrorFallback')
            );
        },
    });

    const runApply = useCallback(() => {
        if (applySubs.length === 0) return;
        applyMutation.mutate();
    }, [applySubs.length, applyMutation]);

    return {
        open,
        openDialog,
        close,
        levelId,
        setLevelId,
        replaceChanged,
        setReplaceChanged,
        previewQuery,
        preview,
        /** Number of grants the unticked checkbox is leaving alone. */
        changedCount: changedSubs.length,
        impactRows,
        applySubs,
        runApply,
        applyBusy: applyMutation.isPending,
    };
}

export type BulkLevelAssignVM = ReturnType<typeof useBulkLevelAssign>;
